import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, InputField, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { PortalUserDocument, RevokeMembershipDocument } from '../../graphql/__generated__/types';
import { revokeRefusalOf } from './revokeRefusal';

interface Props {
    membershipId: string;
    organizationName: string;
    roleName: string;
    /** Le nom de la personne, pour que la confirmation dise sur qui elle agit. */
    personName: string;
    onClose: () => void;
}

/**
 * La confirmation de « fermer l'accès » d'une personne sur un compte (PCG-357).
 *
 * **Fermer n'est pas supprimer.** Le geste passe le rattachement en statut « fermé » et
 * garde tout l'historique : il se rouvre ensuite (le geste miroir, `reinstateMembership`).
 * La confirmation le dit, pour qu'un opérateur ne le prenne pas pour une suppression.
 *
 * **Le motif est facultatif et il vaut la peine.** C'est la seule chose qu'un futur
 * lecteur, ou celui qui rouvrira, aura pour savoir pourquoi l'accès a été coupé : la
 * fiche l'affiche ensuite, et le dialog de réouverture le donne à lire avant le clic. On
 * l'offre donc ici, sans l'exiger : l'API accepte une fermeture sans motif.
 *
 * **Attention, fermer le DERNIER accès d'une personne annule son invitation en attente.**
 * L'API le fait quand c'est son dernier rattachement actif : sa prochaine requête est
 * refusée. On le dit, parce que c'est le seul effet qui déborde du compte visé.
 *
 * Les refus (le dernier administrateur, son propre accès, le compte hors portefeuille)
 * ne se pré-emptent pas : ils demandent des faits que cette requête ne porte pas, donc
 * ils se lisent après le clic, comme ceux de la réouverture.
 */
const RevokeMembershipDialog = ({ membershipId, organizationName, roleName, personName, onClose }: Props) => {
    const [reason, setReason] = useState('');

    const [revoke, { data, loading, error }] = useMutation(RevokeMembershipDocument, {
        // La fiche imprime le statut de chaque rattachement et l'état de l'invitation :
        // les deux changent ici. Sans le refetch l'opérateur relirait l'état qu'il vient
        // de quitter.
        refetchQueries: [PortalUserDocument],
        awaitRefetchQueries: true,
    });

    const submit = () => {
        // Le refus se lit sur `error` ci-dessous : sans ce catch, la promesse rejetée
        // remonterait en « unhandled » à chaque refus.
        const trimmed = reason.trim();
        revoke({ variables: { membershipId, reason: trimmed.length > 0 ? trimmed : null } }).catch(() => {});
    };

    /* ------------------------------------------------------------------- done */
    if (data?.revokeMembership) {
        return (
            <Modal open onClose={onClose} title={c('Revoke access').t`Access closed`}>
                <ModalContent>
                    <Alert type="success" title={c('Revoke access').t`${organizationName} is closed to this person.`}>
                        <p className="text-sm">
                            {c('Revoke access')
                                .t`Their access to this account is closed. Nothing is deleted: the closure is on record with its date and reason, and it can be reopened later from this sheet.`}
                        </p>
                    </Alert>
                </ModalContent>
                <ModalFooter>
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Close`}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }

    /* ---------------------------------------------------------------- confirm */
    return (
        <Modal open onClose={onClose} title={c('Revoke access').t`Close this access`}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">
                        <b>{personName}</b>
                        <span className="text-text-600">
                            {' '}
                            · {organizationName} · {roleName}
                        </span>
                    </p>

                    <Alert type="warning">
                        {c('Revoke access')
                            .t`Closing removes this person's access to ${organizationName}. It is not a deletion: the closure keeps its history and can be reopened later. If this is their last open access, the pending invitation is cancelled and their next request is refused.`}
                    </Alert>

                    <InputField
                        id="revoke-reason"
                        label={c('Revoke access').t`Reason (optional)`}
                        type="text"
                        value={reason}
                        onValue={setReason}
                        placeholder={c('Revoke access').t`Left the company, access no longer needed…`}
                    />
                    {/* Le motif est ce que la réouverture donnera à lire avant le clic :
                        court, mais c'est la seule trace de la décision. */}
                    <p className="text-xs text-text-600">
                        {c('Revoke access')
                            .t`It is journalled and shown when someone considers reopening the access. Leave it empty if there is nothing to add.`}
                    </p>

                    {error && <RefusalNotice refusal={revokeRefusalOf(error)} />}
                </div>
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button category="destructive" onClick={submit} loading={loading}>
                    {c('Revoke access').t`Close the access`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default RevokeMembershipDialog;
